//common
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useLoaderData } from "react-router-dom";
import { FunnelFill } from "react-bootstrap-icons";
import { List, RowComponentProps, ListImperativeAPI } from "react-window";
import * as THREE from "three";

//satellite
import { AbandonedVehicleStatus } from "./api/Network";
import { AbandonedVehicleLoaderData, Match } from "./loaders/abandonedVehicleLoader";
import { Button } from "./components/Button";
import { StatusFilterModal } from "./components/CompareFilterModal";
import { abandonedVehicleStatuses } from "./components/AbandonedVehicleStatusArray";
import { Api } from "./api/Api";
import { AbandonedVehicleItemMemo, Row } from "./AbandonedVehicleItem";
import { vehicleThumbnailBefore, vehicleThumbnailAfter } from "./api/Url";
import { toDeg, toRad } from "./common/Orbital";
import { carPaints, initOrthoSatelliteRenderer, renderOrthoSatelliteImageBitmap, rotateSatelliteBitmap } from "./AbandonedVehicleReviewPageViewer";
import { PairBlendCanvas } from "./components/PairBlendCanvas";
import { expandAbandonedVehicle } from "./loaders/abandonedVehicleCompressed";
import { CommandCard } from "./CommandCard";
import { showAbandonedVehicleStatus } from "./api/EnumUtils";
import { AnnotationFilter, AnnotationFilterModal } from "./components/AnnotationFilter";

const initialValue: AnnotationFilter = {
    typeFilters:      [],
    situationFilters: [],
    statusFilters:    abandonedVehicleStatuses,
    confThr:          0.0,
    similarityThr:    0.0,
}

// row height for the virtualized list - 6em at the default 16px root
const ROW_HEIGHT = 124;

export function AbandonedVehicleReviewPage() {
    const [showFilters,   setShowFilters] = useState<boolean>( false );
    const [filters,       setFilters] = useState<AnnotationFilter>( initialValue );
    const { vehicles: initVehicles } = useLoaderData() as AbandonedVehicleLoaderData;
    const [vehicles,      setVehicles] = useState<Match[]>( initVehicles );
    const [ind,           setInd] = useState<number>();
    const [tool,          setTool] = useState<AbandonedVehicleStatus>();
    const [urlIds, setUrlIdOverride] = useState<Set<number>>();

    //refs
    const rendererRef = useRef<[HTMLCanvasElement, THREE.WebGLRenderer] | null>( null );
    const listRef     = useRef<ListImperativeAPI>( null );

    const getRenderer = useCallback( function() {
        if( !rendererRef.current ) {
            console.info( "Initializing OrthoSatelliteRenderer" );
            rendererRef.current = initOrthoSatelliteRenderer();
        }
        return rendererRef.current;
    }, [] );

    const [canvas, renderer] = getRenderer();
    function nextVehicle() { setInd( x => x !== undefined ? Math.min( x + 1, vehicles.length - 1 ) : undefined ); }
    function prevVehicle() { setInd( x => x !== undefined ? Math.max( x - 1, 0 )                   : undefined ); }

    const filteredIds = useMemo( () => {
        if( urlIds !== undefined ) {
            return urlIds;
        }
        const all = vehicles.toSorted( ( a, b ) => a.abandonedVehicleId - b.abandonedVehicleId );
        return new Set( all
               .filter( v => filters.statusFilters.includes( v.status.status ) )
               .filter( v => !filters.confThr || ( v.confidence > filters.confThr ) )
               .filter( v => !filters.similarityThr || ( v.similarity > filters.similarityThr ) )
               .map( v => v.abandonedVehicleId )
        );
    }, [filters, urlIds] );

    const resultsFiltered = useMemo( () => {
        return vehicles
            .filter( v => filteredIds.has( v.abandonedVehicleId ) )
            .toSorted( ( a, b ) => a.abandonedVehicleId - b.abandonedVehicleId );
    }, [vehicles, filteredIds] );

    // resolve ?vehicleId=... from the URL to an index into resultsFiltered (runs once / on data change)
    useEffect( () => {
        const params = new URLSearchParams( window.location.search );
        const rawIds = params.get("ids");
        if( rawIds ) {
            const ids = rawIds.split( "," ).map( Number ).filter( n => !Number.isNaN( n ) );
            if( ids.length > 0 ) {
                setUrlIdOverride( new Set( ids ) );
            }
        }
        const raw = params.get( "vehicleId" );
        if( raw === null ) { 
            return;
        }
        const id = Number( raw );
        if( Number.isNaN( id ) ) { return; }
        const i = resultsFiltered.findIndex( v => v.abandonedVehicleId === id );
        if( i >= 0 ) { setInd( i ); }
    }, [] );

    // keep the URL in sync with the current selection so it can be shared / reloaded
    useEffect( () => {
        const url = new URL( window.location.href );
        if( ind !== undefined && resultsFiltered[ind] ) {
            url.searchParams.set( "vehicleId", String( resultsFiltered[ind].abandonedVehicleId ) );
        }
        else {
            url.searchParams.delete( "vehicleId" );
        }
        window.history.replaceState( null, "", url.toString() );
    }, [ind, resultsFiltered] );

    const handleMatchClick = useCallback( async ( match: Match ) => {
        setInd( resultsFiltered.findIndex( x => x.abandonedVehicleId === match.abandonedVehicleId ) );
    }, [resultsFiltered] );

    const selected = ind !== undefined ? resultsFiltered[ind] : undefined;
    function update( match: Match, status: AbandonedVehicleStatus ) {
        const result = Api.abandonedVehicleUpdate( match.abandonedVehicleId, {
            note:   "",
            status: status
        } );
        result.then( res => {
            if( !res.isOk ) {
                throw new Error( "Error saving vehicle" );
            }
            const vehicle = expandAbandonedVehicle( res.value );
            setTool( undefined );
            setVehicles( prev => prev.map( v => v.abandonedVehicleId === vehicle.abandonedVehicleId ? vehicle : v ) );
            nextVehicle();
        } );
        return;
    }

    useEffect( () => {
        const handleKeyDown = ( e: KeyboardEvent ) => {
            if( selected === undefined ) { return; }
            switch( e.key ) {
                case "z": setTool( AbandonedVehicleStatus.Candidate                   ); break; 
                case "c": setTool( AbandonedVehicleStatus.ReviewedUnsure              ); break; 
                case "v": setTool( AbandonedVehicleStatus.ReviewedVerified            ); break; 
                case "a": setTool( AbandonedVehicleStatus.ReviewedRejectedIdentity    ); break; //not same car
                case "s": setTool( AbandonedVehicleStatus.ReviewedRejectedMoved       ); break; //same car but not in same position
                case "d": setTool( AbandonedVehicleStatus.ReviewedRejectedNotAVehicle ); break; //both are not a vehicle at all                
                case "f": setTool( AbandonedVehicleStatus.ReviewedRejectedArrived     ); break; //vehicle present in b but not a
                case "g": setTool( AbandonedVehicleStatus.ReviewedRejectedDeparted    ); break; //vehicle present in a but not b
                case "Enter": {
                    if( tool !== undefined ) { update( selected, tool ); }
                    break;
                }

                //prev and next
                case "ArrowDown": nextVehicle(); e.preventDefault(); break;
                case "ArrowUp":   prevVehicle(); e.preventDefault(); break;
            }
        };
        window.addEventListener( "keydown", handleKeyDown );
        return () => window.removeEventListener( "keydown", handleKeyDown );
    }, [selected, tool] );

    // when ind moves (e.g. via n/m keys), scroll the virtual list to keep it visible
    useEffect( () => {
        if( ind === undefined || !listRef.current ) { return; }
        const sel = resultsFiltered[ind];
        if( !sel ) { return; }
        const visibleIdx = resultsFiltered.findIndex( v => v.abandonedVehicleId === sel.abandonedVehicleId );
        if( visibleIdx >= 0 ) { listRef.current.scrollToRow( { index: visibleIdx, align: "smart" } ); }
    }, [resultsFiltered, ind] );

    return <div className="h-100">
        <AnnotationFilterModal
            show={showFilters}
            filters={filters}
            setFilters={setFilters}
            onHide={() => setShowFilters( false )} />

        <div id="capture-list-page">
            <div id="capture-list">
                <div className="bg-body-tertiary border border-end-0 d-flex flex-row w-100 justify-content-center align-items-center gap-2 p-2">
                    <div className="flex-grow-1 ps-2 fs-5">
                        {ind} / {resultsFiltered.length} Detections
                    </div>
                    <Button className="bg-body fs-6 px-2 py-1 rounded" onClick={ () => setShowFilters( true ) }>
                        <FunnelFill />
                    </Button>
                </div>
                <div style={{ minHeight: 0, height: "100%" }}>
                    <List
                        listRef={listRef}
                        rowComponent={Row}
                        rowCount={resultsFiltered.length}
                        rowHeight={ROW_HEIGHT}
                        rowProps={{ selectedId: selected?.abandonedVehicleId, vehicles: resultsFiltered, onClick: handleMatchClick }}
                        overscanCount={6} />
                </div>
            </div>
            <div className="bg-black text-body">                
                {selected
                    ? <BeforeAfterView vehicle={selected} tool={tool} canvas={canvas} renderer={renderer} />
                    : <div className="text-muted">Select a detection to view before / after.</div>}
            </div>
        </div>
    </div>;
}

interface BeforeAfterViewProps {
    vehicle:  Match;
    tool:     AbandonedVehicleStatus | undefined;   
    canvas:   HTMLCanvasElement;
    renderer: THREE.WebGLRenderer;
}

function Thumbnail( { url, label }: { url: string; label: string } ) {
    return <div className="d-flex flex-column">
        <div className="text-light text-center">{label}</div>
        <img
            className="border border-2 rounded-4"
            src={url}
            alt={label}
            style={{
                imageRendering: "auto",
                height:         "256px",
                width:          "256px"
            }} />
    </div>;
}

function BeforeAfterView( { vehicle, tool, canvas, renderer }: BeforeAfterViewProps ) {
    const [carPaint, setCarPaint] = useState<THREE.MeshPhysicalMaterial>( carPaints.white );
    const [model,      setModel]      = useState<string>( "/000680.glb" );
    const [flip,       setFlip]       = useState<number>( 0 );
    const [renderBitmaps, setRenderBitmaps] = useState<[ImageBitmap, ImageBitmap]>();
    
    useEffect( () => {
        return () => {
            renderBitmaps?.forEach( b => b.close() );
        };
    }, [renderBitmaps] );

    useEffect( () => {
        let cancelled = false;
        async function run() {
            let before: ImageBitmap | undefined;
            let after: ImageBitmap | undefined;

            try {
                const size    = 32;
                const outSize = 32;
                const ratio   = 1;
                const beforeBitmap = await renderOrthoSatelliteImageBitmap( canvas, renderer, {
                    glbUrl:           model,
                    groundTextureUrl: vehicleThumbnailBefore( vehicle.abandonedVehicleId ),
                    vehicleAzimuth:   toDeg( vehicle.angle + toRad( flip ) ),
                    satAzimuth:       85.8,
                    satElevation:     87.9,
                    sunAzimuth:       152.1,
                    sunElevation:     63.0,
                    width:            size,
                    height:           size,
                    pixelRatio:       ratio,
                    carPaint:         carPaint,
                } );

                const beforeRotated = await rotateSatelliteBitmap( beforeBitmap, 85.8, outSize, outSize );                
                const afterBitmap = await renderOrthoSatelliteImageBitmap( canvas, renderer, {
                    glbUrl:           model,
                    groundTextureUrl: vehicleThumbnailAfter( vehicle.abandonedVehicleId ),
                    vehicleAzimuth:   toDeg( vehicle.angle + toRad( flip ) ),
                    satAzimuth:       331.7,
                    satElevation:     78.8,
                    sunAzimuth:       224.2,
                    sunElevation:     63.9,
                    width:            size,
                    height:           size,
                    pixelRatio:       ratio,
                    carPaint:         carPaint,
                } );                

                const afterRotated = await rotateSatelliteBitmap( afterBitmap, 331.7, outSize, outSize );                
                if( cancelled ) {
                    beforeRotated.close();
                    afterRotated.close();
                    return;
                }
                setRenderBitmaps( [beforeRotated, afterRotated] );
            } catch( err ) {
                console.error( err );
            } finally {
                before?.close();
                after?.close();
            }
        }
        run();
        return () => {
            cancelled = true;
        };
    }, [vehicle.abandonedVehicleId, vehicle.angle, model, flip, carPaint] );

    const ab = useMemo<[string, string]>( () => [
        vehicleThumbnailBefore( vehicle.abandonedVehicleId ),
        vehicleThumbnailAfter( vehicle.abandonedVehicleId )
    ], [vehicle] );  

    //don't show anything until we are ready
    if( !renderBitmaps ) {
        return <></>;
    }
    return <div className="h-100 position-relative d-flex flex-column align-items-center gap-3">
        <div className="mt-4 fs-5">Vehicle #{vehicle.abandonedVehicleId}</div>
        <div className="d-flex gap-3">
            <Thumbnail url={vehicleThumbnailBefore( vehicle.abandonedVehicleId )} label="Before" />
            <Thumbnail url={vehicleThumbnailAfter( vehicle.abandonedVehicleId )}  label="After" />
        </div>
        <div className="d-flex gap-3">
            <PairBlendCanvas
                height={256}
                width={256}
                ab={ab}
                cd={renderBitmaps} />
        </div>        
        <div className="d-flex flex-row small gap-2 w-50 justify-content-center">
            <Button className="flex-grow-1" onClick={ () => setFlip( ( flip + 180 ) % 360 ) }>Flip</Button>
            <Button className="flex-grow-1" onClick={ () => setModel( "/000680.glb" ) }>Car</Button>
            <Button className="flex-grow-1" onClick={ () => setModel( "/000248.glb" ) }>SUV</Button>
            <Button className="flex-grow-1" onClick={ () => setModel( "/000450.glb" ) }>Minivan</Button>
            <Button className="flex-grow-1" onClick={ () => setModel( "/000549.glb" ) }>Pickup</Button>
        </div>
        <div className="d-flex flex-row small gap-2 w-50 justify-content-center">
            <Button className="flex-grow-1" onClick={ () => setCarPaint( carPaints.white )}>White</Button>
            <Button className="flex-grow-1" onClick={ () => setCarPaint( carPaints.black )}>Black</Button>
            <Button className="flex-grow-1" onClick={ () => setCarPaint( carPaints.silver )}>Silver</Button>
            <Button className="flex-grow-1" onClick={ () => setCarPaint( carPaints.red )}>Red</Button>
            <Button className="flex-grow-1" onClick={ () => setCarPaint( carPaints.blue )}>Blue</Button>
        </div>
        <div style={{ fontSize: "13px" }}>
            <CommandCard cmdSlots={ [
                null, null, null, null, null,
                { key: "a", active: false, color: "#faa", label: "Not Same" },
                { key: "s", active: false, color: "#faa", label: "Moved"    },
                { key: "d", active: false, color: "#faa", label: "No Veh"   },
                { key: "f", active: false, color: "#faa", label: "Arrive"   },
                { key: "g", active: false, color: "#faa", label: "Depart"   },
                //bottom row
                { key: "z", active: false, color: "#ffa", label: "Candid"   },
                null,
                { key: "c", active: false, color: "#ddd", label: "Unsure"   },
                { key: "v", active: false, color: "#0f0", label: "Verify"   },
            ] }
            onSelect={ () => {} } />
        </div>
        {tool && <div>
            Press Enter to Mark as "{showAbandonedVehicleStatus( tool )}"
        </div>}
        {/* <div className="text-muted small">
            conf {vehicle.confidence?.toFixed( 2 ) ?? "—"} · sim {vehicle.similarity?.toFixed( 2 ) ?? "—"}
        </div> */}
    </div>;
}